/**
 * The marks the editor draws for reasons of its own.
 *
 * Two of them, both about whitespace: a dot in every space a selection covers,
 * and a line down each level of indentation. What they have in common is that
 * they are worked out from the document and the selection rather than from the
 * language, so they live here and are tested without an editor.
 *
 * The guide is a background image on the line, drawn as a gradient with one
 * hairline per level, because a guide runs the height of the line and a
 * character's own box is shorter than that. The dot cannot be a background at
 * all: the browser paints a selection over the backgrounds of the characters
 * in it, so a dot drawn there would be under the highlight that asks for it.
 * Where the spaces are is worked out here; it is drawn above the text, in the
 * layer the editor keeps for the cursor at the end of a selection.
 *
 * The colours come from the palette in `app.css`.
 */
import { Decoration } from '@codemirror/view';
import type { EditorState, Range } from '@codemirror/state';

/** How many spaces one level of indentation is. */
export const INDENT = 2;

/** One line's part of a selection, by the edges the editor measured for it. */
export interface Band {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/**
 * What a corner of a band is.
 *
 * `round` is an outside corner, `glued` is where the band above or below ends
 * at the same place so the two read as one box, and `inner` is the inside of a
 * step, where the neighbouring band reaches further out and wraps around this
 * one, which curves the other way.
 */
export type Corner = 'round' | 'glued' | 'inner';

/** The corners of a band, in the order `top-left top-right bottom-right
 * bottom-left`, which is the order `border-radius` takes them. */
export type Corners = [Corner, Corner, Corner, Corner];

/**
 * How the corners of every band curve, given the bands of one selection from
 * top to bottom.
 *
 * Each corner is compared with the band on that side of it: a neighbour that
 * ends in the same place makes a straight join, one that falls short leaves a
 * corner to round, and one that reaches further out makes the inside of a step.
 */
export function bandCorners(bands: Band[]): Corners[] {
  const cornerOf = (band: Band, neighbour: Band | undefined, side: 'left' | 'right'): Corner => {
    if (neighbour === undefined) {
      return 'round';
    }
    const gap = neighbour[side] - band[side];
    if (Math.abs(gap) < 1) {
      return 'glued';
    }
    const reachesOut = side === 'right' ? gap > 0 : gap < 0;
    return reachesOut ? 'inner' : 'round';
  };
  return bands.map((band, index) => [
    cornerOf(band, bands[index - 1], 'left'),
    cornerOf(band, bands[index - 1], 'right'),
    cornerOf(band, bands[index + 1], 'right'),
    cornerOf(band, bands[index + 1], 'left'),
  ]);
}

/**
 * The spaces the selection covers, between `from` and `to`.
 *
 * The editor draws a dot in each of them, which it can only do if it knows
 * where they are.
 */
export function selectedSpaces(state: EditorState, from: number, to: number): number[] {
  const spaces: number[] = [];
  for (const range of state.selection.ranges) {
    const start = Math.max(range.from, from);
    const end = Math.min(range.to, to);
    for (let pos = start; pos < end; pos += 1) {
      if (state.sliceDoc(pos, pos + 1) === ' ') {
        spaces.push(pos);
      }
    }
  }
  return spaces;
}

/**
 * A line down each level of the indentation the lines between `from` and `to`
 * start with.
 *
 * A line shows the levels it is indented by. An empty one has no indentation of
 * its own, so it shows the levels the lines around it agree on, which is what
 * keeps a guide running through a blank line inside a block and stopping where
 * the block ends.
 */
export function indentGuides(state: EditorState, from: number, to: number): Range<Decoration>[] {
  const marks: Range<Decoration>[] = [];
  const indents: (number | null)[] = [];
  const first = state.doc.lineAt(from).number;
  const last = state.doc.lineAt(to).number;
  for (let number = first; number <= last; number += 1) {
    const line = state.doc.line(number);
    const own = leadingSpaces(line.text);
    indents.push(line.text.trim() === '' ? null : own);
  }
  for (let index = 0; index < indents.length; index += 1) {
    const line = state.doc.line(first + index);
    const levels = levelsAt(indents, index);
    if (levels > 0) {
      marks.push(Decoration.line({ attributes: { style: guideStyle(levels) } }).range(line.from));
    }
  }
  return marks;
}

/**
 * The levels a line shows, from its own indentation or from the block it is in.
 *
 * A guide marks the block a line sits in, not the line itself, so the line's
 * own level is left out. A line indented one level shows none: a guide two
 * columns from the start would sit against its first character, which is what
 * every editor this is imitating avoids.
 */
function levelsAt(indents: (number | null)[], index: number): number {
  const own = indents[index] ?? null;
  if (own !== null) {
    return Math.max(0, Math.floor(own / INDENT) - 1);
  }
  const before = nearest(indents, index, -1);
  const after = nearest(indents, index, 1);
  return Math.max(0, Math.floor(Math.min(before, after) / INDENT) - 1);
}

/** The indentation of the nearest line that has any, in one direction. */
function nearest(indents: (number | null)[], index: number, step: number): number {
  for (let at = index + step; at >= 0 && at < indents.length; at += step) {
    const indent = indents[at] ?? null;
    if (indent !== null) {
      return indent;
    }
  }
  return 0;
}

/** How many spaces a line starts with, which is what its indentation is. */
function leadingSpaces(text: string): number {
  let count = 0;
  while (text[count] === ' ') {
    count += 1;
  }
  return count;
}

/** The rules that draw `levels` guides down a line, one every two columns. */
function guideStyle(levels: number): string {
  const stops: string[] = [];
  let previous = '0';
  for (let level = 1; level <= levels; level += 1) {
    const at = `${level * INDENT}ch`;
    stops.push(`transparent ${previous} ${at}`, `var(--code-guide) ${at} calc(${at} + 1px)`);
    previous = `calc(${at} + 1px)`;
  }
  // The last colour of a gradient runs on to the end of the box, so the line
  // has to end transparent or the rest of it is painted with the guide's.
  stops.push(`transparent ${previous} 100%`);
  return `background-image: linear-gradient(to right, ${stops.join(', ')})`;
}
